import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { CsvDownloadButton } from '@/components/venue/CsvDownloadButton'
import { RevenueReservationListTable } from '@/components/venue/RevenueReservationListTable'
import { RevenueSummaryCards } from '@/components/venue/RevenueSummaryCards'
import { useCourts } from '@/hooks/useCourts'
import { useVenueRevenue } from '@/hooks/useVenueRevenue'
import { cn } from '@/lib/utils/cn'
import { validateReservationPeriod } from '@/lib/venue/reservationViewModel'
import type { VenueRevenueStatus } from '@/lib/venue/revenueTypes'
import {
  REVENUE_PRESET_OPTIONS,
  REVENUE_STATUS_OPTIONS,
  csvExportRangeFromFilter,
  defaultRevenueListFilter,
  lastMonthPeriod,
  thisMonthPeriod,
  type RevenueListFilter,
  type RevenuePreset,
} from '@/lib/venue/revenueViewModel'
import { downloadVenueSettlementCyclesCsv } from '@/lib/venue/settlementApi'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 매출 현황 — 'sales/revenue'(설계 §2). 결제 시점 수수료 스냅샷 기준(#792). 기간 프리셋(이번달/지난달/
 * 직접지정) + 코트 + 상태(4값) 필터로 조회하면 요약 카드 4종 + 예약별 목록(커서 "더 보기")을 보여준다.
 *
 * 🚨 기존 회차 축(`/settlements/revenue`) 페이지를 이 EP로 데이터 소스 교체했다(설계 §2 "사전 확인" —
 * "이번 달" 프리셋은 서버가 아니라 클라이언트가 KST로 계산해 항상 구체 날짜로 보낸다, 자율 결정 3).
 */
export function VenueSalesRevenuePage() {
  const { venueId } = useVenueContext()

  const [appliedFilter, setAppliedFilter] = useState<RevenueListFilter>(defaultRevenueListFilter)
  const [selectedPreset, setSelectedPreset] = useState<RevenuePreset>('THIS_MONTH')
  const [customInputs, setCustomInputs] = useState({ from: '', to: '' })

  const courtsQuery = useCourts(venueId)
  const revenueQuery = useVenueRevenue(venueId, appliedFilter)

  const periodError = validateReservationPeriod(customInputs)
  const courts = courtsQuery.data?.courts.map((court) => ({ courtId: court.courtId, name: court.name })) ?? []
  const pages = revenueQuery.data?.pages ?? []
  const items = pages.flatMap((page) => page.reservations)
  const lastPage = pages.at(-1)

  function selectPreset(preset: RevenuePreset) {
    setSelectedPreset(preset)
    if (preset === 'THIS_MONTH') {
      setAppliedFilter((current) => ({ ...current, ...thisMonthPeriod() }))
    } else if (preset === 'LAST_MONTH') {
      setAppliedFilter((current) => ({ ...current, ...lastMonthPeriod() }))
    }
  }

  function applyCustomRange() {
    if (periodError) return
    setAppliedFilter((current) => ({ ...current, from: customInputs.from, to: customInputs.to }))
  }

  function toggleStatus(status: VenueRevenueStatus) {
    setAppliedFilter((current) => ({
      ...current,
      statuses: current.statuses.includes(status)
        ? current.statuses.filter((value) => value !== status)
        : [...current.statuses, status],
    }))
  }

  return (
    <>
      <PageHeader
        title="매출 현황"
        description="결제 시점에 적용된 수수료 기준으로 예약별 매출을 조회합니다."
        action={
          <CsvDownloadButton
            label="정산 회차 CSV 다운로드"
            onDownload={async () => {
              await downloadVenueSettlementCyclesCsv(venueId, csvExportRangeFromFilter(appliedFilter))
            }}
          />
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div role="group" aria-label="조회 기간 프리셋" className="flex gap-2">
          {REVENUE_PRESET_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={selectedPreset === option.value}
              onClick={() => {
                selectPreset(option.value)
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                selectedPreset === option.value
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {selectedPreset === 'CUSTOM' ? (
          <>
            <div>
              <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-revenue-from">
                시작일
              </label>
              <Input
                id="sales-revenue-from"
                type="date"
                value={customInputs.from}
                onChange={(event) => {
                  setCustomInputs((current) => ({ ...current, from: event.target.value }))
                }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-revenue-to">
                종료일
              </label>
              <Input
                id="sales-revenue-to"
                type="date"
                value={customInputs.to}
                onChange={(event) => {
                  setCustomInputs((current) => ({ ...current, to: event.target.value }))
                }}
              />
            </div>
            <Button size="md" disabled={Boolean(periodError)} onClick={applyCustomRange}>
              조회
            </Button>
          </>
        ) : null}

        <div className="w-40">
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-revenue-court">
            코트
          </label>
          <Select
            id="sales-revenue-court"
            value={appliedFilter.courtId}
            onChange={(event) => {
              const value = event.target.value
              setAppliedFilter((current) => ({
                ...current,
                courtId: value === 'ALL' ? 'ALL' : Number(value),
              }))
            }}
          >
            <option value="ALL">전체 코트</option>
            {courts.map((court) => (
              <option key={court.courtId} value={court.courtId}>
                {court.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {selectedPreset === 'CUSTOM' && periodError ? (
        <p className="mb-3 text-xs text-danger-500">{periodError}</p>
      ) : null}

      <div role="group" aria-label="예약 상태 필터" className="mb-4 flex flex-wrap gap-2">
        {REVENUE_STATUS_OPTIONS.map((option) => {
          const selected = appliedFilter.statuses.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                toggleStatus(option.value)
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {revenueQuery.isLoading ? <LoadingState rows={4} label="매출 현황을 불러오는 중" /> : null}

      {revenueQuery.isError ? (
        <ErrorState
          error={revenueQuery.error}
          onRetry={() => {
            void revenueQuery.refetch()
          }}
        />
      ) : null}

      {lastPage ? (
        <div className="space-y-4">
          <RevenueSummaryCards summary={lastPage.summary} />

          <p className="text-sm text-slateish-600">전체 {lastPage.totalCount.toLocaleString('ko-KR')}건</p>

          <RevenueReservationListTable
            items={items}
            hasNextPage={revenueQuery.hasNextPage ?? false}
            isFetchingNextPage={revenueQuery.isFetchingNextPage}
            onLoadMore={() => {
              void revenueQuery.fetchNextPage()
            }}
          />
        </div>
      ) : null}
    </>
  )
}
